import { useEffect, useMemo, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { useQuery } from "@tanstack/react-query"

// ** UI Components
import PmsLoader from "@components/common/loader"
import DashboardLayout from "@pma/app/dashboard/layout"
import { Separator } from "@components/common/separator"
import { DataTable } from "@components/common/data-table/data-table"
import { Card, CardContent, CardTitle } from "@components/common/card"

// ** API Routes
import { GET_ALL_FOLDERS_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Store
import { useAuth } from "@stores/auth-store"

// ** Types
import { DictType } from "@pma/types"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"

// ** Custom Components
import FolderCard from "@components/files/folder-card"
import SearchFile from "@components/files/search-file"
import { FileCard } from "@components/files/file-card"
import FilesMenu from "@components/files/files-page-menu"
import DeletedFiles from "@components/files/deleted-files"
import AllUserFiles from "@components/files/all-user-files"
import PreviewFolder from "@components/files/preview-folder"
import { filesColumn } from "@components/files/files-table-columns"

export default function FilesPage() {
  const { translate } = useTranslator()
  const { authConfig, setActiveFolder } = useAuth()

  const [deleted, setDeleted] = useState(false)
  const [viewFiles, setViewFiles] = useState(false)
  const [folderSearch, setFolderSearch] = useState("")
  const [folderList, setFolderList] = useState<any[]>([])
  const [layout, setLayout] = useState<"grid" | "table">(() => {
    const savedLayout = localStorage.getItem("fileViewLayout")
    return savedLayout === "table" ? "table" : "grid"
  })
  const [search, setSearch] = useState({ pending: true, text: "", results: { items: [] } })

  const gridView = layout === "grid"
  const activeFolder = authConfig?.activeFolder
  const isPreviewOpen = !!activeFolder?.id
  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const filteredFolderList = folderSearch.trim()
    ? folderList.filter((folder) =>
        folder?.name?.toLowerCase().includes(folderSearch.toLowerCase()),
      )
    : folderList

  useEffect(() => {
    localStorage.setItem("fileViewLayout", layout)
  }, [layout])

  const getFolders = async () => {
    const response = await axios.get(
      `${SERVER_URL}${GET_ALL_FOLDERS_ENDPOINT}?organization_id=${orgId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response?.data
  }

  const {
    data,
    isLoading,
    refetch: refetchAllFolder,
  } = useQuery({
    queryKey: ["project-folders"],
    queryFn: getFolders,
  })

  function buildFolderHierarchy(treeData: any, foldersData: any) {
    const folderDetailsMap = new Map()
    foldersData.forEach((folder: any) => {
      folderDetailsMap.set(folder.id, folder)
    })

    function _buildNode(currentTreeNodeId: any, currentTreeNodeValue: any) {
      const folderInfo = folderDetailsMap.get(currentTreeNodeId)

      if (!folderInfo) {
        return null
      }

      const node = { ...folderInfo }
      node.sub = []

      if (typeof currentTreeNodeValue === "object" && currentTreeNodeValue !== null) {
        for (const childIdStr in currentTreeNodeValue) {
          if (Object.prototype.hasOwnProperty.call(currentTreeNodeValue, childIdStr)) {
            try {
              const childId = parseInt(childIdStr, 10)
              const childValue = currentTreeNodeValue[childIdStr]
              const childBuiltNode = _buildNode(childId, childValue)
              if (childBuiltNode) {
                node.sub.push(childBuiltNode)
              }
            } catch (error) {
              console.log("error >>>", error)
            }
          }
        }
      }

      return node
    }

    const resultTree = []

    // Basic validation for treeData input.
    if (typeof treeData !== "object" || treeData === null || Array.isArray(treeData)) {
      console.error("Error: treeData must be a non-null object.")
      return []
    }

    for (const rootIdStr in treeData) {
      if (Object.prototype.hasOwnProperty.call(treeData, rootIdStr)) {
        try {
          const rootId = parseInt(rootIdStr, 10) // Parse string ID to integer
          const rootValue = treeData[rootIdStr]

          // Build the node for each identified root folder.
          const builtRootNode = _buildNode(rootId, rootValue)

          // If the root node was successfully built, add it to the final result.
          if (builtRootNode) {
            resultTree.push(builtRootNode)
          }
        } catch (error) {
          console.log("error >>>", error)
        }
      }
    }

    return resultTree
  }

  useEffect(() => {
    if (data) {
      const newArray = buildFolderHierarchy(data.tree, data.folders)
      setFolderList(newArray)
    }
  }, [data])

  const handleMenuOptions = (option: "delete" | "layout", value: any) => {
    if (option === "delete") {
      setDeleted(value)
    } else {
      setViewFiles(false)
      setDeleted(false)
      setLayout(value)
    }
  }

  return (
    <DashboardLayout>
      {activeFolder?.id && (
        <PreviewFolder projectId={activeFolder?.project_id} refetchAllFolder={refetchAllFolder} />
      )}
      <div className={cn('flex flex-1 flex-col gap-4 p-4 pb-12 md:gap-8', isPreviewOpen && 'hidden')}>
        <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
          <div className='flex shrink-0 items-center gap-2'>
            {search.text ? (
              <h1 className='min-w-fit text-lg font-semibold md:text-2xl'>
                {translate("Showing results for") + " " + `"${search?.text}"`}
              </h1>
            ) : deleted ? (
              <h1 className='min-w-fit text-lg font-semibold md:text-2xl'>
                {translate("Deleted Files")}
              </h1>
            ) : (
              <>
                <h1
                  className={cn(
                    "min-w-fit cursor-pointer transition-all ease-in-out hover:scale-110 md:text-2xl",
                    viewFiles ? "" : "text-lg font-semibold",
                  )}
                  onClick={() => setViewFiles(false)}
                >
                  {translate("Folders")}
                </h1>
                /
                <h1
                  className={cn(
                    "min-w-fit cursor-pointer transition-all ease-in-out hover:scale-110 md:text-2xl",
                    viewFiles ? "text-lg font-semibold" : "",
                  )}
                  onClick={() => setViewFiles(true)}
                >
                  {translate("Files")}
                </h1>
              </>
            )}
          </div>

          <div className='flex flex-col items-center gap-4 md:flex-row'>
            {viewFiles ? (
              <SearchFile search={search} setSearch={setSearch} />
            ) : (
              !deleted &&
              !search.text && (
                <input
                  type='text'
                  value={folderSearch}
                  onChange={(e) => setFolderSearch(e.target.value)}
                  placeholder={translate("Search folders")}
                  className='h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:w-48 md:w-64'
                />
              )
            )}
            <FilesMenu
              options={{
                layout,
                deleted: deleted,
              }}
              handleMenuOptions={handleMenuOptions}
            />
          </div>
        </div>

        {search?.text && !search?.pending ? (
          <Card className='border-0 pb-4 shadow-none'>
            <CardContent className='flex flex-col gap-3 py-2'>
              <CardTitle>
                {search?.text}- {search.results?.items?.length} {translate("found")}
              </CardTitle>

              <div className='grid grid-cols-4 gap-4'>
                {search.results?.items?.map((file: any) => (
                  <FileCard file={file?.item} key={file?.id} />
                ))}
              </div>
            </CardContent>
            <Separator />
          </Card>
        ) : (
          <>
            {isLoading ? (
              <PmsLoader />
            ) : (
              <>
                {deleted ? (
                  <DeletedFiles />
                ) : (
                  <>
                    {viewFiles ? (
                      <AllUserFiles />
                    ) : (
                      <>
                        {gridView ? (
                          <div className='grid grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-2 md:gap-1 lg:grid-cols-3 2xl:grid-cols-6'>
                            {filteredFolderList?.map((folder: DictType) => (
                              <FolderCard
                                setActiveFolder={setActiveFolder}
                                key={folder?.id}
                                folder={folder}
                              />
                            ))}
                          </div>
                        ) : (
                          <DataTable
                            data={filteredFolderList}
                            isLoading={isLoading}
                            columns={filesColumn}
                            iszIndexApply={true}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  )
}
